import { useState } from "react";
import styled from "styled-components";
import { FooterButtonContainer, StyledModal } from "../components/styled-modal";
import { Button } from "@interstate/components/Button";
import { Card } from "@interstate/components/Card";
import { VehicleCardType } from "@coxauto-ui/cxm/communications/api";
import { CenteredSpinner } from "@coxauto-ui/core/components";
import {
  vehicleModalCancelButtonTestId,
  vehicleModalContainerTestId,
  vehicleModalInsertButtonTestId
} from "../../testIds";

export enum VehicleTokenType {
  VIN_BROCHURE = "vin-brochure",
  PRIMARY_VEHICLE_CARD = "primary-vehicle-card",
  SIMILAR_NEW_VEHICLE_CARDS = "similar-new-vehicle-cards",
  SIMILAR_USED_VEHICLE_CARDS = "similar-used-vehicle-cards",
  SIMILAR_NEW_USED_VEHICLE_CARDS = "similar-new-used-vehicle-cards"
}

interface VehicleOption {
  id: VehicleTokenType;
  label: string;
  cardType?: VehicleCardType;
  hidden?: boolean;
}

export const VEHICLE_OPTIONS: VehicleOption[] = [
  { id: VehicleTokenType.VIN_BROCHURE, label: "Vin Brochure" },
  {
    id: VehicleTokenType.PRIMARY_VEHICLE_CARD,
    label: "Primary Vehicle Card",
    cardType: VehicleCardType.Primary
  },
  {
    id: VehicleTokenType.SIMILAR_NEW_VEHICLE_CARDS,
    label: "Similar New Vehicle Cards",
    cardType: VehicleCardType.SimilarNew
  },
  {
    id: VehicleTokenType.SIMILAR_USED_VEHICLE_CARDS,
    label: "Similar Used Vehicle Cards",
    cardType: VehicleCardType.SimilarUsed
  },
  {
    id: VehicleTokenType.SIMILAR_NEW_USED_VEHICLE_CARDS,
    label: "Similar New & Used Vehicle Cards",
    cardType: VehicleCardType.SimilarMixed
  }
];

export interface VehicleSelectorModalProps {
  isOpen: boolean;
  /** Whether a vehicle insert is currently in progress */
  isInserting?: boolean;
  onClose: () => void;
  /** Called with the selected option ID and vehicle card type when a vehicle option is confirmed. */
  onVehicleSelect: (
    optionId: VehicleTokenType,
    cardType?: VehicleCardType
  ) => void;
}

const ModalContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: min(500px, 70vh);
  overflow: hidden;
`;

const ScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  padding: 16px 24px;
`;

const CardWrapper = styled.div`
  margin: 6px 0;
  cursor: pointer;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
  border-radius: 8px;
`;

export function VehicleSelectorModal({
  isOpen,
  onClose,
  onVehicleSelect,
  isInserting = false
}: VehicleSelectorModalProps) {
  const [selectedOption, setSelectedOption] = useState<VehicleOption | null>(
    null
  );

  const handleSelect = (option: VehicleOption) => {
    setSelectedOption(option);
  };

  const handleInsert = () => {
    if (selectedOption) {
      onVehicleSelect(selectedOption.id, selectedOption.cardType);
      setSelectedOption(null);
    }
  };

  const handleDoubleClick = (option: VehicleOption) => {
    if (isInserting) return;
    onVehicleSelect(option.id, option.cardType);
    setSelectedOption(null); // reset so the modal reopens with no stale selection if opened again
  };

  const handleClose = () => {
    setSelectedOption(null);
    onClose();
  };

  const modalFooter = (
    <FooterButtonContainer>
      <Button
        buttonStyle="secondary"
        data-testid={vehicleModalCancelButtonTestId}
        disabled={isInserting}
        onClick={handleClose}
      >
        Cancel
      </Button>
      <Button
        buttonStyle="primary"
        data-testid={vehicleModalInsertButtonTestId}
        disabled={selectedOption === null || isInserting}
        onClick={handleInsert}
      >
        {isInserting ? "Inserting..." : "Insert"}
      </Button>
    </FooterButtonContainer>
  );

  return (
    <StyledModal
      containerTestId={vehicleModalContainerTestId}
      footer={modalFooter}
      isOpen={isOpen}
      title="Vehicle"
      onClose={handleClose}
    >
      {isInserting && (
        <LoadingOverlay>
          <CenteredSpinner id="vehicle-card-insert-loading-spinner" />
        </LoadingOverlay>
      )}
      <ModalContentWrapper>
        <ScrollableContent>
          {VEHICLE_OPTIONS.filter(option => !option.hidden).map(option => {
            const isSelected = selectedOption?.id === option.id;
            return (
              <CardWrapper
                data-testid={`vehicle-option-${option.id}`}
                key={option.id}
                onDoubleClick={() => handleDoubleClick(option)}
              >
                <Card
                  content={option.label}
                  primaryAction={() => handleSelect(option)}
                  sx={{
                    fontSize: "15px",
                    fontWeight: 500,
                    color: "#333333",
                    transition: "background-color 0.2s, border-color 0.2s",
                    ...(isSelected
                      ? {
                          backgroundColor: "#cce8ff",
                          border: "1px solid #2196f3",
                          "&:hover": {
                            backgroundColor: "#e3f2fd",
                            borderColor: "#318ad3"
                          }
                        }
                      : {
                          "&:hover": {
                            backgroundColor: "#f4f4f4",
                            borderColor: "#aaaaaa"
                          }
                        })
                  }}
                />
              </CardWrapper>
            );
          })}
        </ScrollableContent>
      </ModalContentWrapper>
    </StyledModal>
  );
}

export default VehicleSelectorModal;
